"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight, type LucideIcon } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

type NavExtraItem = {
  title: string;
  url: string;
};

type NavSubItem = {
  title: string;
  url: string;
  extraItems?: NavExtraItem[];
  items?: NavSubItem[];
};

type NavItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  isActive?: boolean;
  isAuth?: boolean;
  items?: NavSubItem[];
  extraItems?: NavExtraItem[];
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

function renderNavItem(
  item: NavItem | NavSubItem,
  pathname: string,
  level = 0,
  onLinkClick?: () => void,
) {
  const uniqueKey = `${item.title}-${item.url}-${level}`;
  const isActive =
    item.url === pathname ||
    (item.items && item.items.some((sub) => sub.url === pathname)) ||
    (item.extraItems &&
      item.extraItems.some((extra) => extra.url === pathname));

  if (
    (item.items && item.items.length > 0) ||
    (item.extraItems && item.extraItems.length > 0)
  ) {
    return (
      <Collapsible
        key={uniqueKey}
        asChild
        defaultOpen={isActive}
        className="group/collapsible"
      >
        <SidebarMenuSubItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuSubButton
              isActive={isActive}
              className={`custom-text-size cursor-pointer transition-all duration-200 hover:scale-105 hover:font-semibold hover:text-gray-800 rounded-md hover:bg-gray-50 hover:shadow-md ${
                isActive
                  ? "border shadow-md rounded-md font-semibold bg-white text-black dark:bg-white dark:text-black"
                  : ""
              }`}
            >
              {item.title}
              <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuSubButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub level={level + 1}>
              {item.items?.map((subItem) =>
                // renderNavItem(subItem, pathname, level + 1, onLinkClick),
                renderNavItem(subItem, pathname, level + 1, onLinkClick),
              )}
              {item.extraItems?.map((extraItem) => (
                <SidebarMenuSubItem key={`${extraItem.title}-${extraItem.url}`}>
                  <SidebarMenuSubButton
                    isActive={pathname === extraItem.url}
                    asChild
                    className={
                      pathname === extraItem.url
                        ? "border shadow-md rounded-md font-semibold bg-white text-black dark:bg-white dark:text-black"
                        : ""
                    }
                    onClick={onLinkClick}
                  >
                    <Link href={extraItem.url} onClick={onLinkClick}>
                      {extraItem.title}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuSubItem>
      </Collapsible>
    );
  }

  // Render a simple link for leaf items
  return (
    <SidebarMenuSubItem
      key={uniqueKey}
      className="custom-text-size transition-all duration-200 hover:scale-105 hover:font-semibold hover:text-gray-800 rounded-md hover:bg-gray-50 hover:shadow-md"
    >
      <SidebarMenuSubButton
        isActive={isActive}
        asChild
        className={`custom-text-size transition-all duration-200 hover:scale-105 hover:font-semibold hover:text-gray-800 rounded-md hover:bg-gray-50 hover:shadow-md ${
          isActive
            ? "border shadow-md rounded-md font-semibold bg-white dark:bg-white dark:text-black text-black"
            : ""
        }`}
        onClick={onLinkClick}
      >
        <Link href={item.url}>{item.title}</Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

export function NavMain({ items }: { items: NavGroup[] }) {
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();

  const handleLinkClick = () => {
    setTimeout(() => {
      setOpenMobile(false);
    }, 1000);
  };

  return (
    <div>
      {items.map((group, index) => (
        <SidebarGroup key={`${group.title}-${index}`}>
          <SidebarGroupLabel>{`${group.title}`}</SidebarGroupLabel>
          <SidebarMenu>
            {group.items.map((item) => {
              const itemKey = `${item.title}-${item.url}`;
              const isItemActive =
                item.url === pathname ||
                (item.items &&
                  item.items.some(
                    (subItem) =>
                      subItem.url === pathname ||
                      (subItem.items &&
                        subItem.items.some(
                          (nestedItem) => nestedItem.url === pathname,
                        )) ||
                      (subItem.extraItems &&
                        subItem.extraItems.some(
                          (extra) => extra.url === pathname,
                        )),
                  )) ||
                (item.extraItems &&
                  item.extraItems.some((extra) => extra.url === pathname));

              if (
                (item.items && item.items.length > 0) ||
                (item.extraItems && item.extraItems.length > 0)
              ) {
                return (
                  <Collapsible
                    key={itemKey}
                    asChild
                    defaultOpen={isItemActive}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          isActive={isItemActive}
                          disabled={item.isAuth === false}
                          className="custom-text-size transition-all duration-200 hover:scale-105 hover:font-semibold hover:text-gray-800  rounded-md hover:bg-gray-50 hover:shadow-md"
                        >
                          {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                          {item.title}
                          <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items?.map((subItem) => (
                            <span key={`${subItem.title}-${subItem.url}`}>
                              {renderNavItem(
                                subItem,
                                pathname,
                                1,
                                handleLinkClick,
                              )}
                            </span>
                          ))}
                          {item.extraItems?.map((extraItem) => (
                            <SidebarMenuSubItem
                              key={`${extraItem.title}-${extraItem.url}`}
                            >
                              <SidebarMenuSubButton
                                isActive={pathname === extraItem.url}
                                asChild
                                onClick={handleLinkClick}
                              >
                                <Link
                                  href={extraItem.url}
                                  onClick={handleLinkClick}
                                >
                                  {extraItem.title}
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              }

              return (
                <SidebarMenuItem key={itemKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isItemActive}
                    disabled={item.isAuth === false}
                    className={
                      isItemActive
                        ? "border shadow-md rounded-md font-semibold bg-white text-black dark:bg-white dark:text-black"
                        : ""
                    }
                    onClick={handleLinkClick}
                  >
                    <Link
                      href={item.url}
                      className="custom-text-size transition-all duration-200 hover:scale-105 hover:font-semibold hover:text-gray-800 rounded-md hover:bg-gray-50 hover:shadow-md"
                    >
                      {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                      {item.title}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </div>
  );
}
